/**
 * Custom hooks: encapsulam React Query + chamadas tipadas + lógica de UI.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { api, ping } from './api';
import { auth } from './auth';
import { theme } from './theme';

// ─── Moved Cards Tracker ──────────────────────────────────────────────────
const recentlyMovedCards = new Set<number>();
const recentlyMovedListeners = new Set<() => void>();

export function markCardAsMoved(idPrazo: number) {
  recentlyMovedCards.add(idPrazo);
  recentlyMovedListeners.forEach((l) => l());
  setTimeout(() => {
    recentlyMovedCards.delete(idPrazo);
    recentlyMovedListeners.forEach((l) => l());
  }, 4000); // flash for 4 seconds
}

export function useRecentlyMoved(idPrazo: number): boolean {
  const [isMoved, setIsMoved] = useState(() => recentlyMovedCards.has(idPrazo));
  useEffect(() => {
    const check = () => {
      setIsMoved(recentlyMovedCards.has(idPrazo));
    };
    recentlyMovedListeners.add(check);
    check();
    return () => {
      recentlyMovedListeners.delete(check);
    };
  }, [idPrazo]);
  return isMoved;
}

import type {
  AgendarAudienciaIn,
  AtualizarUsuarioIn,
  CriarAudienciaIn,
  CriarCasoIn,
  CriarPrazoIn,
  EditarPrazoIn,
  CriarUsuarioIn,
  EndpointTransicao,
  KanbanSemana,
  Papel,
  SessaoUsuario,
  PecasStatus,
  RefinementRequest,
  StagingStatusOut,
  TransicaoResponse,
} from './types';

// ─── Papéis ─────────────────────────────────────────────────────────────────
// "ehAdmin" = OPERADOR PLENO da produção (painel completo do card, kanban,
// audiências, caixa). O GERENTE integra o grupo — espelha o contrato do
// backend, onde as rotas operacionais são require_papel("admin","gerente")
// (as só-admin, ex. Financeiro, são gated por papel === 'admin' explícito).
// 'triador' é papel legado normalizado para admin.
export function ehAdmin(papel: Papel | undefined | null): boolean {
  return papel === 'admin' || papel === 'gerente' || papel === 'triador';
}

// ─── Health / status ───────────────────────────────────────────────────────


export function useApiHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: ping,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}


export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth-config'],
    queryFn: api.authConfig,
    staleTime: 5 * 60 * 1000,
  });
}


// ─── Auth ─────────────────────────────────────────────────────────────────


export function useAuth() {
  const [login, setLogin] = useState<SessaoUsuario | null>(auth.getLogin());
  const queryClient = useQueryClient();

  return {
    login,
    setLogin(s: SessaoUsuario) {
      auth.setLogin(s);
      setLogin(s);
      // Invalida tudo — vai re-fetchar autenticado pelo cookie JWT da nova sessão.
      queryClient.invalidateQueries();
    },
    marcarSenhaTrocada() {
      auth.marcarSenhaTrocada();
      setLogin(auth.getLogin());
    },
    logout() {
      void api.logout().catch(() => {});  // limpa o cookie no servidor (best-effort)
      auth.logout();
      setLogin(null);
      queryClient.clear();
    },
  };
}


export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    enabled: auth.isLogged(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * usePode — fundação de permissões por capacidade (papel × tenant).
 * Lê `me.capacidades` (efetivas, já resolvidas no backend; superadmin recebe tudo)
 * e devolve `pode(cap)`. É só cosmético — a trava real é o require_capacidade no backend.
 */
export function usePode() {
  const { data: me } = useMe();
  const caps = new Set(me?.capacidades ?? []);
  return (cap: string) => caps.has(cap);
}

/** Branding do tenant: nome de exibição, ramo, cor — alimenta o TopNav. */
export function useBranding() {
  return useQuery({
    queryKey: ['branding'],
    queryFn: () => api.branding(),
    enabled: auth.isLogged(),
    staleTime: 10 * 60 * 1000,
  });
}

/** Plano do escritório (freemium). `true` enquanto carrega = não pisca cadeado p/ usuários pro. */
export function usePlanoPro(): boolean {
  const { data: me, isLoading } = useMe();
  if (isLoading || !me) return true;
  return (me.escritorio?.plano ?? 'pro') === 'pro';
}


export function useUsuarios(incluirInativos = false) {
  return useQuery({
    queryKey: ['usuarios', incluirInativos],
    queryFn: () => api.usuarios(incluirInativos),
    enabled: auth.isLogged(),
    staleTime: 30 * 60 * 1000,    // muda raramente
  });
}


// ─── Usuários (mutations, admin) ──────────────────────────────────────────


export function useCriarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CriarUsuarioIn) => api.criarUsuario(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });
}


export function useAtualizarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AtualizarUsuarioIn }) =>
      api.atualizarUsuario(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      // papel/cor podem afetar cards (quem assumiu) → invalida prazos também
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}


export function useResetarSenhaUsuario() {
  return useMutation({
    mutationFn: (id: number) => api.resetarSenhaUsuario(id),
  });
}


export function useExcluirUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.excluirUsuario(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      // cards do excluído ficam sem dono → invalida o quadro
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}


// ─── Prazos / Kanban ──────────────────────────────────────────────────────


export function usePrazosSemana(semanaIso: string | undefined) {
  return useQuery({
    queryKey: ['prazos', 'semana', semanaIso],
    queryFn: () => api.prazosSemana(semanaIso),
    enabled: auth.isLogged(),
    staleTime: 30_000,
  });
}


/** Busca várias semanas em paralelo (pra vista mensal e Minha Caixa). */
export function usePrazosVariasSemanas(semanasIso: string[]) {
  return useQuery({
    queryKey: ['prazos', 'multi', ...semanasIso.sort()],
    queryFn: async () => {
      const resultados = await Promise.all(
        semanasIso.map((s) => api.prazosSemana(s).catch(() => null)),
      );
      return resultados.filter((r): r is KanbanSemana => !!r);
    },
    enabled: auth.isLogged() && semanasIso.length > 0,
    staleTime: 30_000,
  });
}


export function usePrazoDetalhe(id: number | null) {
  return useQuery({
    queryKey: ['prazo', id],
    queryFn: () => api.prazoDetalhe(id!),
    enabled: !!id && auth.isLogged(),
    staleTime: 10_000,
  });
}


/** Lista os escritórios (super usuário) — para o seletor de impersonação. */
export function useEscritoriosSU(habilitado: boolean) {
  return useQuery({
    queryKey: ['escritorios-su'],
    queryFn: () => api.listarEscritorios(),
    enabled: habilitado && auth.isLogged(),
    staleTime: 60_000,
  });
}


// ─── Peticionar com modelo ──────────────────────────────────────────────────

/** Status dos modelos .docx de todos os tipos de peça (tabela da esquerda). */
export function useModelosPeca(habilitado = true) {
  return useQuery({
    queryKey: ['modelos-peca'],
    queryFn: () => api.listarModelosPeca(),
    enabled: habilitado && auth.isLogged(),
    staleTime: 60_000,
  });
}

export function useUploadModeloPeca() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, file }: { key: string; file: File }) => api.uploadModeloPeca(key, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['modelos-peca'] }),
  });
}

export function useExcluirModeloPeca() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.excluirModeloPeca(key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['modelos-peca'] }),
  });
}

/** Dados básicos do processo selecionado (metade direita). */
export function useDadosPeticao(idPrazo: number | null) {
  return useQuery({
    queryKey: ['peticao-dados', idPrazo],
    queryFn: () => api.dadosPeticao(idPrazo!),
    enabled: !!idPrazo && auth.isLogged(),
    staleTime: 60_000,
  });
}

/** Conferência de placeholders do modelo escolhido contra os dados do processo. */
export function useConferirPeticao() {
  return useMutation({
    mutationFn: ({ idPrazo, pieceKey }: { idPrazo: number; pieceKey: string }) =>
      api.conferirPeticao(idPrazo, pieceKey),
  });
}

/** Gera o .docx (docxtpl) e salva na pasta do processo no Drive (aba Peticionar com modelo). */
export function useGerarPeticaoModelo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ idPrazo, pieceKey, overrides }: { idPrazo: number; pieceKey: string; overrides: Record<string, string> }) =>
      api.gerarPeticaoModelo(idPrazo, pieceKey, overrides),
    onSuccess: (_d, v) => {
      // a peça virou a do card → atualiza status/arquivos do prazo
      qc.invalidateQueries({ queryKey: ['pecas', v.idPrazo] });
      qc.invalidateQueries({ queryKey: ['arquivos', v.idPrazo] });
    },
  });
}


/** Resumo dos alertas de reconciliação (gap-detector) — p/ o badge na navbar. */
export function useReconciliacaoResumo(habilitado: boolean) {
  return useQuery({
    queryKey: ['reconciliacao-resumo'],
    queryFn: () => api.reconciliacaoResumo(),
    enabled: habilitado && auth.isLogged(),
    staleTime: 5 * 60_000,        // 5 min — o cron roda 1x/dia
    refetchOnWindowFocus: true,
  });
}


/**
 * Busca cards por número de processo em QUALQUER data (não só na janela carregada).
 * Habilita só com >= 3 dígitos. O chamador passa o termo já "debounced".
 */
export function useBuscarPrazos(numero: string) {
  const digitos = (numero || '').replace(/\D/g, '');
  return useQuery({
    queryKey: ['buscar-prazos', digitos],
    queryFn: () => api.buscarPrazos(digitos),
    enabled: digitos.length >= 3 && auth.isLogged(),
    staleTime: 15_000,
  });
}


export function useHistorico(id: number | null) {
  return useQuery({
    queryKey: ['historico', id],
    queryFn: () => api.historico(id!),
    enabled: !!id && auth.isLogged(),
    staleTime: 10_000,
  });
}


export function useKanbanStatus() {
  return useQuery({
    queryKey: ['kanban', 'status'],
    queryFn: api.kanbanStatus,
    enabled: auth.isLogged(),
    staleTime: 30_000,
  });
}


export function useTransicoesPermitidas(id: number | null) {
  return useQuery({
    queryKey: ['transicoes', id],
    queryFn: () => api.transicoesPermitidas(id!),
    enabled: !!id && auth.isLogged(),
    staleTime: 10_000,
  });
}


// ─── Transições (mutations) ───────────────────────────────────────────────


export function useTransicao(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      nome,
      observacao,
    }: {
      nome: EndpointTransicao;
      observacao?: string;
    }): Promise<TransicaoResponse> =>
      api.transicao(idPrazo, nome, observacao),
    onSuccess: () => {
      markCardAsMoved(idPrazo);
      // Invalida todos os queries que possam ter o card desatualizado
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
      queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['historico', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['transicoes', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['kanban', 'status'] });
    },
  });
}


// ─── Criar / excluir card (overhaul v4) ───────────────────────────────────


export function useCriarPrazo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CriarPrazoIn) => api.criarPrazo(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban', 'status'] });
    },
  });
}

// ─── Staging (upload de autos ANTES do card) — popup "Novo Prazo" ──────────

export function useUploadStaging() {
  return useMutation({
    mutationFn: ({ arquivo, onProgress }: { arquivo: File; onProgress?: (pct: number) => void }) =>
      api.uploadStaging(arquivo, onProgress),
  });
}

/** Poll do progresso/sugestões do staging enquanto `token` estiver setado. */
export function useStagingStatus(token: string | null) {
  return useQuery({
    queryKey: ['staging', token],
    queryFn: () => api.stagingStatus(token!),
    enabled: !!token,
    staleTime: 0,
    refetchInterval: (query) => {
      const d = query.state.data as StagingStatusOut | undefined;
      return d?.status === 'processando' ? 2_000 : false;
    },
  });
}

// ─── Cards por processo (modelo perícias: buscar antes de criar) ───────────

/** Busca processos existentes por dígitos do CNJ; habilitada com ≥ 10 dígitos
 *  para não floodar a API enquanto o usuário digita. */
export function useBuscaProcesso(numero: string) {
  const digitos = numero.replace(/\D/g, '');
  return useQuery({
    queryKey: ['processos', 'busca', digitos],
    queryFn: () => api.buscarProcessos(digitos),
    enabled: digitos.length >= 10,
    staleTime: 30_000,
  });
}

/** Todos os jobs (prazos) do processo — seção "Serviços do processo" do modal. */
export function useServicosProcesso(processoId: number | null | undefined) {
  return useQuery({
    queryKey: ['processo', processoId, 'servicos'],
    queryFn: () => api.servicosProcesso(processoId!),
    enabled: !!processoId,
    staleTime: 15_000,
  });
}

/** Substitui o PDF dos autos do processo (autos atualizados). */
export function useSubstituirPdfProcesso(processoId: number | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arquivo, onProgress }: { arquivo: File; onProgress?: (pct: number) => void }) =>
      api.substituirPdfProcesso(processoId!, arquivo, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processo', processoId] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}

/** "Ajustar" um prazo (edita campos de agendamento/identificação). */
export function useEditarPrazo(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EditarPrazoIn) => api.editarPrazo(idPrazo, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban', 'status'] });
    },
  });
}

// ── Casos (aba Petições Iniciais) ───────────────────────────────────────────

/** Kanban da aba Petições Iniciais (só cards origem='peticao_inicial' do tenant). */
export function useCasosKanban() {
  return useQuery({
    queryKey: ['casos', 'kanban'],
    queryFn: () => api.casosKanban(),
    enabled: auth.isLogged(),
    staleTime: 30_000,
  });
}

export function useCriarCaso() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CriarCasoIn) => api.criarCaso(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['casos'] });
    },
  });
}

export function useAnaliseInicial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idPrazo: number) => api.analiseInicial(idPrazo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}


export function useExcluirPrazo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.excluirPrazo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['audiencias'] });
    },
  });
}


// ─── Drive (Fase 5) ───────────────────────────────────────────────────────


export function useDriveStatus() {
  return useQuery({
    queryKey: ['drive', 'status'],
    queryFn: api.driveStatus,
    enabled: auth.isLogged(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}


export function useArquivosPrazo(idPrazo: number | null) {
  return useQuery({
    queryKey: ['arquivos', idPrazo],
    queryFn: () => api.listarArquivos(idPrazo!),
    enabled: !!idPrazo && auth.isLogged(),
    staleTime: 30_000,
    // Overhaul v4 (a): verificação FÍSICA do Drive ao abrir o card — sempre
    // refaz o fetch no mount pra refletir uploads/exclusões feitos por fora.
    refetchOnMount: 'always',
  });
}


/**
 * Verificação física forçada dos arquivos no Drive (POST /verificar-pecas).
 * Limpa IDs obsoletos de mapa/blocos/docx quando os arquivos foram apagados,
 * mantendo os botões coerentes com a realidade física do Drive.
 */
export function useVerificarPecas(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.verificarPecas(idPrazo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
    },
  });
}


export function useUploadPeticao(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (pct: number) => void }) =>
      api.uploadPeticao(idPrazo, file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}


export function useExcluirArquivo(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api.excluirArquivo(idPrazo, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}


// ─── Peças processuais (Fase 9) ──────────────────────────────────────────


/**
 * Busca o status de geração de mapa+rascunho para um prazo.
 * Quando status === 'gerando', faz polling automático a cada 5s.
 * Só ativa para cards em em_andamento (idPrazo não-nulo).
 */
export function usePecasStatus(idPrazo: number | null) {
  return useQuery({
    queryKey: ['pecas', idPrazo],
    queryFn: () => api.pecasStatus(idPrazo!),
    enabled: !!idPrazo && auth.isLogged(),
    staleTime: 30_000,
    refetchInterval: (query) => {
      const d = query.state.data as PecasStatus | undefined;
      if (d?.status === 'gerando' || d?.status === 'pendente_refinamento') return 5_000;
      // Enfileirado: faz polling mais lento p/ pegar quando o worker iniciar (→ gerando).
      if (d?.status === 'pendente') return 12_000;
      return false;
    },
  });
}


export function useMapaConteudo(idPrazo: number | null) {
  return useQuery({
    queryKey: ['mapa-conteudo', idPrazo],
    queryFn: () => api.obterMapaConteudo(idPrazo!),
    enabled: !!idPrazo && auth.isLogged(),
    staleTime: 30_000,
  });
}


/**
 * Poll RÁPIDO (700ms) do status das peças — usado durante "Salvar e fechar" para
 * a barra de progresso refletir os checkpoints reais do versionamento. Compartilha
 * a queryKey ['pecas', id] com usePecasStatus (react-query usa o menor intervalo).
 */
export function usePecasStatusRapido(idPrazo: number | null, ativo: boolean) {
  return useQuery({
    queryKey: ['pecas', idPrazo],
    queryFn: () => api.pecasStatus(idPrazo!),
    enabled: !!idPrazo && ativo && auth.isLogged(),
    refetchInterval: ativo ? 700 : false,
    staleTime: 0,
  });
}


export function useBlocosConteudo(idPrazo: number | null) {
  return useQuery({
    queryKey: ['blocos-conteudo', idPrazo],
    queryFn: () => api.obterBlocosConteudo(idPrazo!),
    enabled: !!idPrazo && auth.isLogged(),
    staleTime: 30_000,
  });
}


/**
 * Blocos VIVOS do workdir, com polling — reflete IMEDIATAMENTE as edições do
 * Claude no terminal de refino (antes do salvar.sh subir ao Drive).
 * Mantém os últimos blocos bons em 404/409 transitório (arquivo em escrita).
 */
export function useBlocosWorkdir(idPrazo: number | null, ativo: boolean) {
  return useQuery({
    queryKey: ['blocos-workdir', idPrazo],
    queryFn: () => api.obterBlocosWorkdir(idPrazo!),
    enabled: !!idPrazo && ativo && auth.isLogged(),
    refetchInterval: ativo ? 1500 : false,
    staleTime: 0,
    retry: false,
    placeholderData: (prev) => prev,
  });
}


export function useTesesPropostas(idPrazo: number | null) {
  return useQuery({
    queryKey: ['teses-propostas', idPrazo],
    queryFn: () => api.obterTesesPropostas(idPrazo!),
    enabled: !!idPrazo && auth.isLogged(),
    staleTime: 15_000,
  });
}


export function useAtualizarArsenal(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (teses: any[]) => api.atualizarArsenal(idPrazo, teses),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teses-propostas', idPrazo] });
    },
  });
}


export function useRefinarRascunho(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: RefinementRequest) => api.refinarRascunho(idPrazo, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
    },
  });
}


/**
 * Fase 12 — dispara o pipeline UNIFICADO "Mapa do Processo" via gerar-processo.sh:
 * Split → OCR → pdftotext → Mapa → Blocos. O status vira 'gerando' (gerando_tipo
 * 'processo') e o polling de 5s do usePecasStatus alimenta a barra de progresso.
 */
export function useIniciarProcesso(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.iniciarProcesso(idPrazo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
    },
  });
}

/**
 * Pipeline DOCAS — Stage A. Dispara o pipeline completo via gerar-pipeline.sh:
 * preparo (split→OCR→classify→pack→renomear) → MAPA lendo o _analise_pack →
 * para no gate (etapa_pipeline='mapa'). O polling de 5s alimenta a barra.
 */
export function useIniciarPipeline(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (peca?: { tipo_peca: string; peca_rotulo: string; paginas_ignorar?: string }) =>
      api.iniciarPipeline(idPrazo, peca),
    onSuccess: () => {
      markCardAsMoved(idPrazo);
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}

/** Re-gera o .docx da última versão (determinístico) — botão "Re-gerar .docx" na Revisão. */
export function useRenderizarPeca(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.renderizarPeca(idPrazo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}

/** Move a etapa MACRO do card (ex.: "Gerar a peça" no Refinamento → 'redigindo'). */
export function useSetEtapa(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (etapa: string) => api.setEtapa(idPrazo, etapa),
    onSuccess: () => {
      markCardAsMoved(idPrazo);
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}

/** Gate de aprovação do mapa → dispara Stage B (blocos → render → anti-IA). */
export function useAprovarMapa(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.aprovarMapa(idPrazo),
    onSuccess: () => {
      markCardAsMoved(idPrazo);
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}

/** DataJud (CNJ) — metadados + movimentos do processo. Sob demanda (modal aberto). */
export function useDatajud(idPrazo: number | null, enabled = true) {
  return useQuery({
    queryKey: ['datajud', idPrazo],
    queryFn: () => api.datajud(idPrazo!),
    enabled: !!idPrazo && enabled && auth.isLogged(),
    staleTime: 5 * 60_000,   // metadados mudam devagar
    retry: false,            // 404 (não achou) não deve repetir
  });
}

/** Dispara geração APENAS do mapa (MAPA_DO_CASO.md) via gerar-mapa.sh. */
export function useIniciarMapa(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.iniciarMapa(idPrazo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
    },
  });
}

/** Dispara geração APENAS dos blocos JSON via gerar-blocos.sh (requer PDF). */
export function useIniciarBlocos(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.iniciarBlocos(idPrazo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
    },
  });
}

/**
 * Dispara OCR do PDF (split + ocrmypdf + pdftotext) via ocr-pdf.sh no VPS.
 * Torna o PDF pesquisável e sobe um .txt ao Drive. gerando_tipo='ocr',
 * timeout 900s no backend (sempre acima do timeout do script).
 */
export function useIniciarOcr(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.iniciarOcr(idPrazo),
    onSuccess: () => {
      // status muda para 'gerando' → polling de 5s assume a partir daqui
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
    },
  });
}

/** Legado: dispara geração de mapa+blocos juntos. Usado pelo "Tentar novamente" em erros genéricos. */
export function useIniciarGeracao(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.iniciarGeracao(idPrazo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
    },
  });
}


/**
 * Dispara geração da petição final via gerar-peticao.sh no VPS.
 * Sem argumento — backend auto-detecta o tipo de peça pelo tipo_prazo do card.
 * O script usa --allowedTools Bash,Read e lê as skills no filesystem do VPS
 * em vez de injetar ~40KB no prompt (evita timeout).
 */
export function useGerarPeticao(idPrazo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.gerarPeticao(idPrazo),
    onSuccess: () => {
      // status muda para 'gerando' → polling de 5s assume a partir daqui
      queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['arquivos', idPrazo] });
    },
  });
}


// ─── Audiências (overhaul v4) ─────────────────────────────────────────────


export function useAudiencias() {
  return useQuery({
    queryKey: ['audiencias'],
    queryFn: api.listarAudiencias,
    enabled: auth.isLogged(),
    staleTime: 60_000,
  });
}


export function useAgendarAudiencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AgendarAudienciaIn }) =>
      api.agendarAudiencia(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audiencias'] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
    },
  });
}


/** Inclusão manual de audiência não cadastrada (POST /api/audiencias). */
export function useCriarAudiencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CriarAudienciaIn) => api.criarAudiencia(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audiencias'] });
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban', 'status'] });
    },
  });
}


// ─── Theme ────────────────────────────────────────────────────────────────


export function useTheme() {
  const [tema, setTema] = useState(theme.current());
  return {
    tema,
    toggle() {
      theme.toggle();
      setTema(theme.current());
    },
  };
}


// ─── Detector mobile ──────────────────────────────────────────────────────


export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    const f = localStorage.getItem('viewPref');
    if (f === 'mobile') return true;
    if (f === 'desktop') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => {
      const f = localStorage.getItem('viewPref');
      if (!f) setIsMobile(mq.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// ─── Financeiro (admin) ──────────────────────────────────────────────────────

export function useConfigFinanceiro(habilitado = true) {
  return useQuery({
    queryKey: ['financeiro', 'config'],
    queryFn: () => api.obterConfigFinanceiro(),
    enabled: habilitado && auth.isLogged(),
    staleTime: 5 * 60_000,
  });
}

export function useSalvarConfigFinanceiro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pct: number) => api.salvarConfigFinanceiro(pct),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['financeiro'] }),
  });
}

export function useSocios(incluirInativos = false) {
  return useQuery({
    queryKey: ['socios', incluirInativos],
    queryFn: () => api.listarSocios(incluirInativos),
    enabled: auth.isLogged(),
    staleTime: 5 * 60_000,
  });
}

export function useCriarSocio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('./types').SocioIn) => api.criarSocio(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['socios'] }); qc.invalidateQueries({ queryKey: ['financeiro'] }); },
  });
}

export function useAtualizarSocio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<import('./types').SocioIn> }) => api.atualizarSocio(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['socios'] }); qc.invalidateQueries({ queryKey: ['financeiro'] }); },
  });
}

export function useExcluirSocio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.excluirSocio(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['socios'] }); qc.invalidateQueries({ queryKey: ['financeiro'] }); },
  });
}

export function useReceitas(f: import('./types').FinReceitaFiltros = {}) {
  return useQuery({
    queryKey: ['receitas', f],
    queryFn: () => api.listarReceitas(f),
    enabled: auth.isLogged(),
    staleTime: 30_000,
  });
}

export function useDashboardFinanceiro() {
  return useQuery({
    queryKey: ['financeiro', 'dashboard'],
    queryFn: () => api.dashboardFinanceiro(),
    enabled: auth.isLogged(),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

function _invalidarFinanceiro(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['receitas'] });
  qc.invalidateQueries({ queryKey: ['financeiro'] });
}

export function useCriarReceita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('./types').ReceitaIn) => api.criarReceita(body),
    onSuccess: () => _invalidarFinanceiro(qc),
  });
}

export function useAtualizarReceita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: import('./types').ReceitaUpdateIn }) => api.atualizarReceita(id, body),
    onSuccess: () => _invalidarFinanceiro(qc),
  });
}

export function useMarcarReceitaRecebida() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, valor }: { id: number; data?: string; valor?: number }) => api.marcarReceitaRecebida(id, data, valor),
    onSuccess: () => _invalidarFinanceiro(qc),
  });
}

export function useExcluirReceita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.excluirReceita(id),
    onSuccess: () => _invalidarFinanceiro(qc),
  });
}

export function usePrefillProcesso() {
  return useMutation({
    mutationFn: (numero: string) => api.prefillProcesso(numero),
  });
}

// ── Custos: monitor de gasto da assinatura (uso de IA por call → R$) ──────────
export function useCustosPainel(escopo: 'tenant' | 'global') {
  return useQuery({
    queryKey: ['custos-painel', escopo],
    queryFn: () => api.custosPainel(escopo),
    enabled: auth.isLogged(),
    refetchInterval: 10_000,
    retry: false,    // escopo=global p/ não-superadmin → 403 (não retentar)
  });
}

export function useCustosEventos(escopo: 'tenant' | 'global', limit = 40) {
  return useQuery({
    queryKey: ['custos-eventos', escopo, limit],
    queryFn: () => api.custosEventos(escopo, limit),
    enabled: auth.isLogged(),
    refetchInterval: 10_000,
    retry: false,
  });
}

/** GET /config é superadmin-only → isError marca "não é dono da plataforma". */
export function useCustosConfig() {
  return useQuery({
    queryKey: ['custos-config'],
    queryFn: () => api.custosConfig(),
    enabled: auth.isLogged(),
    retry: false,
  });
}
